"use client";

import { useState } from "react";
import { Send, CheckCircle, Loader2 } from "lucide-react";

export function ContactForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Client-side validation
    if (!formData.subject) {
      setError("נא לבחור נושא");
      return;
    }
    if (formData.message.length < 10) {
      setError("ההודעה חייבת להכיל לפחות 10 תווים");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        if (data.issues) {
          const issueMessages = data.issues
            .map(
              (issue: { path: string[]; message: string }) =>
                `${issue.path.join(".")}: ${issue.message}`,
            )
            .join(", ");
          throw new Error(
            issueMessages || data.error || "Failed to send message",
          );
        }
        throw new Error(data.error || "Failed to send message");
      }

      setIsSubmitted(true);
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  if (isSubmitted) {
    return (
      <div className="rounded-xl border border-green-200 bg-green-50 p-6 text-center">
        <CheckCircle className="mx-auto mb-3 h-12 w-12 text-green-500" />
        <h2 className="mb-2 text-lg font-semibold text-green-900">
          ההודעה נשלחה
        </h2>
        <p className="mb-3 text-sm text-green-700">
          תודה שפנית אלינו. נחזור אליך בהקדם.
        </p>
        <button
          onClick={() => setIsSubmitted(false)}
          className="text-sm text-green-700 underline hover:text-green-900"
        >
          שליחת הודעה נוספת
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-3"
      suppressHydrationWarning
    >
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <label
            htmlFor="name"
            className="mb-1 block text-xs font-medium text-neutral-700"
          >
            שם
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="w-full rounded-lg border border-neutral-300 px-2.5 py-1.5 text-sm transition-all outline-none focus:border-transparent focus:ring-2 focus:ring-neutral-900"
            placeholder="השם שלך"
            suppressHydrationWarning
          />
        </div>
        <div>
          <label
            htmlFor="email"
            className="mb-1 block text-xs font-medium text-neutral-700"
          >
            אימייל
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
            className="w-full rounded-lg border border-neutral-300 px-2.5 py-1.5 text-sm transition-all outline-none focus:border-transparent focus:ring-2 focus:ring-neutral-900"
            placeholder="your@email.com"
            suppressHydrationWarning
          />
        </div>
      </div>

      <div>
        <label
          htmlFor="subject"
          className="mb-1 block text-xs font-medium text-neutral-700"
        >
          נושא
        </label>
        <select
          id="subject"
          name="subject"
          value={formData.subject}
          onChange={handleChange}
          required
          className="w-full rounded-lg border border-neutral-300 px-2.5 py-1.5 text-sm transition-all outline-none focus:border-transparent focus:ring-2 focus:ring-neutral-900"
        >
          <option value="">בחר נושא</option>
          <option value="api-beta">API - רישום לבטא</option>
          <option value="shopify-beta">שופיפיי - רישום לבטא</option>
          <option value="feature">הצעה לתכונה</option>
          <option value="bug">דיווח על תקלה</option>
          <option value="question">שאלה</option>
          <option value="other">אחר</option>
        </select>
      </div>

      <div>
        <label
          htmlFor="message"
          className="mb-1 block text-xs font-medium text-neutral-700"
        >
          הודעה
        </label>
        <textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          required
          minLength={10}
          rows={3}
          className="w-full resize-none rounded-lg border border-neutral-300 px-2.5 py-1.5 text-sm transition-all outline-none focus:border-transparent focus:ring-2 focus:ring-neutral-900"
          placeholder="פרט את פנייתך כאן... (לפחות 10 תווים)"
        />
      </div>

      <button
        type="submit"
        disabled={isSubmitting}
        className="flex w-full items-center justify-center gap-2 rounded-lg bg-neutral-900 px-4 py-2.5 text-sm text-white transition-colors hover:bg-neutral-800 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            שולח...
          </>
        ) : (
          <>
            <Send className="h-4 w-4" />
            שליחה
          </>
        )}
      </button>
    </form>
  );
}
